/**
 * The legacy (unused) adult calorie algorithm as it stood before TECH-2815,
 * kept so we can answer "what would we have recommended before?" for a cat we hold today.
 */
import {
  BodyType,
  IMealPlan,
  LifeStyle,
  PouchSize,
} from "../cat-calculations.types";
import { MEAL_PLAN_CALORIES } from "../cat-calculations.constants";

type LegacyBodyType = "SKINNY" | "JUST_RIGHT" | "CHUBBY" | "OBESE" | "UNKNOWN";
type LegacyLifeStyle = "INACTIVE" | "ACTIVE" | "EXPLORER" | "UNKNOWN";

const LEGACY_BODY_TYPE_CALORIE_MULTIPLIER: Record<LegacyBodyType, number> = {
  SKINNY: 0.75,
  JUST_RIGHT: 1,
  CHUBBY: 1.25,
  OBESE: 1.5,
  UNKNOWN: 1,
};

const LEGACY_LIFESTYLE_CALORIE_MULTIPLIER: Record<LegacyLifeStyle, number> = {
  INACTIVE: 0.8,
  ACTIVE: 1,
  EXPLORER: 1.2,
  UNKNOWN: 1,
};

const LEGACY_CALORIES_PER_KG_PER_DAY = 50;

/** The old symmetric rounding, kept separate from today's asymmetric +15/-35. */
const LEGACY_CALORIE_THRESHOLD = 25;

/** Today's five-point body scale folded onto the old four-point one.
 * VERY_THIN had no old equivalent, so is mapped to SKINNY.
 */
export const LEGACY_BODY_TYPE_BY_BODY_TYPE: Record<BodyType, LegacyBodyType> = {
  VERY_THIN: "SKINNY",
  THIN: "SKINNY",
  JUST_RIGHT: "JUST_RIGHT",
  OVERWEIGHT: "CHUBBY",
  VERY_OVERWEIGHT: "OBESE",
  UNKNOWN: "UNKNOWN",
};

/** Today's four-point activity scale folded onto the old three-point one.
 * INDOOR_LOW_ACTIVITY had no old equivalent, so is mapped to INACTIVE.
 */
export const LEGACY_LIFESTYLE_BY_ACTIVITY_LEVEL: Record<LifeStyle, LegacyLifeStyle> = {
  INDOOR_LOW_ACTIVITY: "INACTIVE",
  INDOOR_HIGH_ACTIVITY: "INACTIVE",
  INDOOR_AND_OUTDOOR: "ACTIVE",
  MOSTLY_OUTDOOR: "EXPLORER",
  UNKNOWN: "UNKNOWN",
};

export const calculateLegacySuggestedCaloriesForGrownCat = (
  weightInKg: number,
  bodyType: BodyType,
  lifestyle: LifeStyle,
): number => {
  const bodyTypeDivisor =
    LEGACY_BODY_TYPE_CALORIE_MULTIPLIER[LEGACY_BODY_TYPE_BY_BODY_TYPE[bodyType]];
  const lifestyleMultiplier =
    LEGACY_LIFESTYLE_CALORIE_MULTIPLIER[LEGACY_LIFESTYLE_BY_ACTIVITY_LEVEL[lifestyle]];
  const idealBodyWeight = (weightInKg / bodyTypeDivisor) * lifestyleMultiplier;

  return Math.round(idealBodyWeight * LEGACY_CALORIES_PER_KG_PER_DAY);
};

/** The old tray size mapping, on the old symmetric threshold. */
export const calculateLegacyTraySize = (caloriesNeeded: number): IMealPlan => {
  for (const [ size, calories ] of Object.entries(MEAL_PLAN_CALORIES)) {
    if (caloriesNeeded <= calories + LEGACY_CALORIE_THRESHOLD) {
      return { size: size as PouchSize, calories };
    }
  }

  return { size: "F", calories: MEAL_PLAN_CALORIES.F };
};
